import axios from 'axios';
import { API_BASE_URL, base_url } from '../config';
import { reinitializeApi } from '../services/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Regular login with email/password
export async function loginWithEmail(email, password) {
  try {
    // Add domain if needed, similar to the PHP version
    const fullEmail = email.includes('@') ? email : `${email}@silulumanzi.com`;
    
    const response = await api.post('/login/process', {
      email: fullEmail,
      password: password
    });
    
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

// Regular login with username/password
export async function loginWithUsername(username, password) {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/auth_login`, {
      email: username, // Changed key from 'username' to 'email'
      password
    });
    
    const data = response.data;
    
    if (data.status === 1) {
      // Store auth token and user data
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    
    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}



// Request password reset
export async function forgotPassword(email) {
  try {
    const response = await api.post('/login/forgot', {
      email
    });
    return response.data;
  } catch (error) {
    console.error('Forgot password error:', error);
    throw error;
  }
}

// Request OTP
export async function requestOTP(phone) {
  try {
    const response = await api.post('/login/otp', {
      phone
    });
    return response.data;
  } catch (error) {
    console.error('OTP request error:', error);
    throw error;
  }
}

// Verify OTP
export async function verifyOTP(phone, otp) {
  try {
    const response = await api.post('/login/otp/verify', {
      phone,
      otp
    });
    
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    
    return response.data;
  } catch (error) {
    console.error('OTP verification error:', error);
    throw error;
  }
}

// Reset password using token
export async function resetPassword(token, password, confirmPassword) {
  try {
    const response = await api.post('/login/reset-password', {
      token,
      password,
      confirm_password: confirmPassword
    });
    return response.data;
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
}

// Microsoft OAuth login - using existing Login.php controller
export async function loginWithMicrosoft() {
  try {
    const reactAppUrl = window.location.origin;
    // Send the React app URL as the "devOrigin" parameter
    // This will be used to redirect back after authentication
    
    // Use API_BASE_URL to respect the current environment (localhost, quality, or production)
    // This ensures authentication happens on the same server as the frontend
    window.location.href = `${API_BASE_URL}/login/azure?devOrigin=${encodeURIComponent(reactAppUrl)}`;
    
    return true;
  } catch (error) {
    console.error('Error initiating Microsoft login:', error);
    throw error;
  }
}

// Update loginWithAzure to also include devOrigin for consistency
export async function loginWithAzure() {
  try {
    const reactAppUrl = window.location.origin;
    // Add devOrigin parameter to the existing Azure login
    window.location.href = `${API_BASE_URL}/login/azure?devOrigin=${encodeURIComponent(reactAppUrl)}`;
    return true;
  } catch (error) {
    console.error('Azure login error:', error);
    throw error;
  }
}

// Handle the callback from PHP
export function handleAuthCallback() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Support both parameter formats for backward compatibility
    let authToken = urlParams.get('authToken') || urlParams.get('token');
    let userDataStr = urlParams.get('userData') || urlParams.get('user');
    
    console.log('Auth callback parameters:', {
      authToken: authToken ? 'present' : 'missing',
      userDataStr: userDataStr ? 'present' : 'missing'
    });
    
    if (authToken && userDataStr) {
      try {
        // Parse user data
        const userData = JSON.parse(decodeURIComponent(userDataStr));
        
        // Store auth token and user data from Microsoft auth (live server)
        localStorage.setItem('microsoft_token', authToken);
        localStorage.setItem('microsoft_user', JSON.stringify(userData));
        
        // Set flag indicating this is Microsoft authentication
        localStorage.setItem('auth_method', 'microsoft');

        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        
        return {
          user: userData,
          token: authToken,
          authMethod: 'microsoft'
        };
      } catch (error) {
        console.error('Error parsing auth callback data:', error);
        throw error;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error in handleAuthCallback:', error);
    return null;
  }
}

// Sync Microsoft authentication to local server
export async function syncMicrosoftAuthToLocal() {
  try {
    const microsoftUser = localStorage.getItem('microsoft_user');
    if (!microsoftUser) {
      throw new Error('No Microsoft user data found');
    }
    
    const userData = JSON.parse(microsoftUser);
    console.log('Syncing Microsoft auth to local server for user:', userData.user_email);
    
    // Authenticate with local server using the user email
    // This will create a local session and token for the same user
    const response = await axios.post(`${API_BASE_URL}/api/auth_login`, {
      email: userData.user_email,
      microsoft_auth: true, // Flag to indicate this is Microsoft auth sync
      microsoft_user: userData // Pass the full user data
    });
    
    if (response.data.status === 1) {
      // Store local auth token and user data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Reinitialize API to use local server with new token
      reinitializeApi();
      
      console.log('Successfully synced Microsoft auth to local server');
      return response.data;
    } else {
      throw new Error(response.data.message || 'Failed to sync authentication');
    }
  } catch (error) {
    console.error('Failed to sync Microsoft auth to local server:', error);
    throw error;
  }
}

// Check if user is authenticated
export function isAuthenticated() {
  return localStorage.getItem('token') !== null;
}

// Get current user
export function getCurrentUser() {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  
  try {
    return JSON.parse(userStr);
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
}

export async function verifySession() {
  try {
    const response = await api.get('/api/check_session');
    if (response.data.status === 1) {
      return response.data.user;
    }
    return null;
  } catch (error) {
    console.error("Session verification failed:", error);
    return null;
  }
}

export function logout() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  // No redirect here, let the caller handle it.
}

// Check if user has specific role
export function hasRole(roleName) {
  const user = getCurrentUser();
  return user && user.role_name === roleName;
}

// Check if user has specific permission
export function hasPermission(permission) {
  const user = getCurrentUser();
  if (!user || !user.permissions) return false;
  
  // Handle serialized permissions (like in PHP)
  const permissions = Array.isArray(user.permissions) 
    ? user.permissions 
    : JSON.parse(user.permissions);
    
  return permissions.includes(permission);
}
